import { useMemo, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * ConsentQrPosterModal — one-tap printable poster patients can scan
 * from their ABHA app to approve the pending consent themselves.
 *
 * The QR encodes a compact JSON envelope so any modern ABHA / PHR
 * scanner can parse it directly:
 *
 *     {
 *       "type": "upchaarq.abdm.consent.v1",
 *       "consent_id": "<uuid>",
 *       "hospital_id": "<uuid>",
 *       "patient_name": "…",
 *       "purpose": "…",
 *       "expires_at": "ISO"
 *     }
 *
 * A short backup URL is also encoded — falls through to the receptionist
 * dashboard consent status page for scanners that expect a URL not a
 * JSON blob.
 */
export default function ConsentQrPosterModal({ open, onClose, consent, hospitalName }) {
  const printRef = useRef(null);

  const qrValue = useMemo(() => {
    if (!consent) return "";
    const envelope = {
      type: "upchaarq.abdm.consent.v1",
      consent_id: consent.id,
      hospital_id: consent.hospital_id,
      patient_name: consent.patient_name,
      purpose: consent.purpose,
      expires_at: consent.expires_at,
      demo_otp: consent.demo_otp || undefined,
    };
    return JSON.stringify(envelope);
  }, [consent]);

  const backupUrl = useMemo(() => {
    if (!consent) return "";
    // Base URL is the same origin the frontend is served from — patients
    // scanning from the same LAN / mobile network hit the receptionist
    // dashboard status page.
    return `${window.location.origin}/abdm/c/${consent.id}`;
  }, [consent]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>ABHA Consent QR · ${(consent?.patient_name || "").slice(0, 30)}</title>
<style>
* { margin:0; padding:0; box-sizing:border-box;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: Georgia, "Times New Roman", serif; color:#0f172a;
       background:#fff; padding: 24px; text-align:center; }
.frame { border: 3px double #0f172a; padding: 22px 16px; max-width: 480px;
         margin: 0 auto; }
.brand { font-size: 10px; text-transform: uppercase; letter-spacing: 3px;
         color:#64748b; margin-bottom: 4px; }
h1 { font-size: 20px; margin-bottom: 4px; }
h2 { font-size: 13px; font-weight: normal; color: #475569; margin-bottom: 14px; }
.qr { display:inline-block; padding: 12px; background:#fff;
      border: 2px solid #0f172a; margin: 8px 0 12px; }
.qr svg { display:block; }
.instr { text-align:left; font-size: 12px; color:#0f172a;
         background:#f8fafc; border-left: 4px solid #10b981;
         padding: 10px 12px; margin-top: 10px; line-height: 1.5; }
.instr ol { padding-left: 18px; }
.instr li { margin: 3px 0; }
.hindi { font-size: 11px; color:#475569; margin-top: 8px;
         font-family: "Noto Sans Devanagari", "Mangal", sans-serif; }
.meta { font-size: 10px; color:#64748b; margin-top: 12px; }
.footer { font-size: 9px; color:#94a3b8; margin-top: 16px;
          border-top: 1px dashed #cbd5e1; padding-top: 8px; }
@media print { body { padding: 0; } .no-print { display:none; } }
</style>
</head>
<body>${printRef.current.innerHTML}</body>
</html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  if (!consent) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="consent-qr-poster">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            Consent QR poster
          </DialogTitle>
        </DialogHeader>

        <div ref={printRef}>
          <div className="frame">
            <p className="brand">UpchaarQ · ABDM Compliant</p>
            <h1>{hospitalName || "Hospital"}</h1>
            <h2>ABHA Consent Request</h2>

            <div className="qr" data-testid="consent-qr-svg">
              <QRCodeSVG value={qrValue} size={200} level="M" includeMargin={false} />
            </div>

            <p style={{ fontSize: 13, fontWeight: "bold" }}>
              {consent.patient_name || "Patient"}
            </p>
            <p style={{ fontSize: 11, color: "#475569" }}>
              Purpose: {consent.purpose}
            </p>

            <div className="instr">
              <strong>How to approve</strong>
              <ol>
                <li>Open the ABHA / NDHM app on your phone</li>
                <li>Tap <em>Scan Consent</em> and scan this QR</li>
                <li>Review the purpose and approve to share your records</li>
              </ol>
              <p className="hindi">
                कैसे स्वीकार करें: ABHA ऐप खोलें → "Scan Consent" → यह QR स्कैन करें → रिकॉर्ड साझा करने के लिए स्वीकार करें।
              </p>
            </div>

            <p className="meta">
              Consent ID: <span style={{ fontFamily: "monospace" }}>{consent.id.slice(0, 8)}…</span>
              &nbsp;·&nbsp;
              Expires: {new Date(consent.expires_at).toLocaleDateString("en-IN", {
                day: "numeric", month: "short",
              })}
            </p>

            <p className="footer">
              Trouble scanning? Visit{" "}
              <span style={{ fontFamily: "monospace" }}>{backupUrl}</span>
              &nbsp;or ask the receptionist.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handlePrint}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="consent-qr-print-btn">
            <Printer size={14} className="mr-1" /> Print Poster
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
